import { Spacing } from '@persona-id/design-tokens';
import produce from 'immer';
import { capitalize, keyBy } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider } from 'react-hook-form';
import { useRouteMatch } from 'react-router';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import {
  CasesGridComponentTypes,
  CasesGridModuleTypes,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/constants';
import {
  ButtonPanel,
  CaseEmailPanel,
  CaseInfoPanel,
  ChecklistPanel,
  DataDisplayPanel,
  DataTablePanel,
  DevicePanel,
  GraphV2Panel,
  InputPanel,
  InputSelectPanel,
  InsightsSummaryPanel,
  KeyValuePanel,
  LocationPanel,
  MarkdownPanel,
  ResourceAccountPanel,
  ResourceInquiryPanel,
  ResourceReportPanel,
  ResourceTransactionPanel,
  ResourceVerificationPanel,
  TablePanel,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels';
import AISummaryPanel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/AISummaryPanel';
import CaseCommentPanel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/CaseCommentPanel';
import IframePanel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/IframePanel';
import LayoutByAccountPanel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/LayoutByAccountPanel';
import ResourceReportRunPanel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/ResourceReportRunPanel';
import SARPanel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/SARPanel';
import LayoutEditor from 'dashboard/apps/DashboardLayoutEditor/LayoutEditor';
import { generateCaseDataManagerJsonSchema } from 'dashboard/components/CaseDataManagerSchemaSelect/utils';
import ContextMenu from 'dashboard/components/ContextMenu';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { decorateFieldSchemas } from 'dashboard/components-lib/FieldSchemaEditorForm/utils';
import NavBreadcrumb from 'dashboard/components-lib/NavBreadcrumb';
import FormEditCaseLayoutTab from 'dashboard/forms/FormEditCaseLayoutTab';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { reorder } from 'dashboard/lib/dnd/utils';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { CaseQuery } from 'dashboard/models/case-query';
import {
  CaseCommentTemplate,
  CaseDataManagerSchema,
  CaseEmailTemplate,
  CaseLayout,
  CaseLayoutTab,
  CaseModule,
  CaseState,
  CaseTemplate,
  CaseTemplateStatus,
  ChecklistItem,
  generateCaseCommentTemplateToken,
  generateCaseEmailTemplateToken,
  SARNarrativeTemplate,
} from 'dashboard/models/case-template';
import { CaseVariableDefinition } from 'dashboard/models/case-variable-definition';
import { Environment } from 'dashboard/models/environment';
import { ExternalIntegrationDataSource } from 'dashboard/models/external-integration';
import { Graph } from 'dashboard/models/graph';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { Workflow } from 'dashboard/models/workflow';
import CaseModuleBasePreview from 'dashboard/modules/CaseModuleBase/CaseModuleBasePreview';
import { ModuleResourceAccount } from 'dashboard/modules/ResourceAccount';
import { ModuleResourceInquiry } from 'dashboard/modules/ResourceInquiry';
import { ModuleResourceReport } from 'dashboard/modules/ResourceReport';
import { ModuleResourceReportRun } from 'dashboard/modules/ResourceReportRun';
import { ModuleResourceTransaction } from 'dashboard/modules/ResourceTransaction';
import { ModuleResourceVerification } from 'dashboard/modules/ResourceVerification';
import AutocorrectChecklistItemsDescription from 'dashboard/views/DashboardCaseTemplatesShowView/AutocorrectChecklistItemsDescription';
import { FormAddCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormAddCaseQuery';
import { FormEditCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormEditCaseQuery';
import CaseTemplateQueryEditor from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateQueryEditor/CaseTemplateQueryEditor';
import CaseTemplateSettings from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/CaseTemplateSettings';
import { CaseTemplateSettingsFormData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/FormCaseTemplateSettings/types';
import { generateDefaultCaseModuleConfiguration } from 'dashboard/views/DashboardCaseTemplatesShowView/helpers';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import useConfirmation from 'lib/hooks/useConfirmation';
import { useExpandCollapseNavbar } from 'lib/hooks/useExpandCollapseNavbar';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchemaObject } from 'lib/json-schema';
import { Permission } from 'lib/permissions';
import { permissionErrorString } from 'lib/permissions-errors';
import { useFormSecurely } from 'lib/security/reactHookForm';
import { getOnlyLastPath } from 'lib/utils/url';

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StatusWrapper = styled.div`
  display: flex;
  padding-left: ${Spacing[2]};
  height: 22px;
`;

const CASE_TEMPLATE_EDITOR_ROOT_PATH = '/dashboard/case-templates/:caseTemplateId';
const CASE_QUERY_EDITOR_PATH = `${CASE_TEMPLATE_EDITOR_ROOT_PATH}/settings/queries/:caseVariableDefinitionId`;

const invalidChecklistItems = (
  checklistItems: ChecklistItem[],
  layoutEditorPath: boolean,
  statesFormState: CaseState[],
) => {
  const invalidItems = {
    invalidStates: new Set<CaseState>(),
    invalidChecklistItems: new Set<ChecklistItem>(),
  };
  if (layoutEditorPath || statesFormState.length === 0) {
    // Skip calculation when on the layout editor path because states cannot be changed on that page.
    return invalidItems;
  }

  const statesMap = keyBy('token', statesFormState);
  checklistItems.forEach((item: ChecklistItem) =>
    item['required-for'].forEach(
      (stateId: string) =>
        statesMap[stateId]['archived-at'] &&
        invalidItems.invalidChecklistItems.add(item) &&
        invalidItems.invalidStates.add(statesMap[stateId]),
    ),
  );

  return invalidItems;
};

export interface DashboardCaseTemplatesShowViewProps {
  organization: Organization;
  currentEnvironment: Environment;
  caseTemplate: CaseTemplate;
  caseDataManagerSchema: CaseDataManagerSchema;
  caseVariableDefinitions: CaseVariableDefinition[];
  tags: Tag[];
  users: User[];
  currentUser: User;
  accountStatuses: AccountStatus[];
  actionWorkflows: Workflow[];
  dataSources: ExternalIntegrationDataSource[];
  caseQueries: CaseQuery[];
  enabledInquiryTemplates: (InquiryTemplate | Template)[];
  publishedVerificationTemplates: VerificationTemplate[];
  accountTypeFilterOptions: AccountTypesFilterOption[];
  graph: Graph;
  graphQueryTemplates: GraphQueryTemplate[];
  isLoadingCaseVariableDefinitions: boolean;
  isLayoutDirty: boolean;
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
  updateCaseTemplateV2: (caseTemplate: CaseTemplate) => void;
  saveCaseTemplateSettings: (formValues: CaseTemplateSettingsFormData) => Promise<void>;
  saveCaseTemplateV2: () => void;
  setCaseTemplateIsValid: (valid: boolean) => void;
  handleCreateCaseQuery: (values: FormAddCaseQueryData) => Promise<void>;
  handleUpdateCaseQuery: (values: FormEditCaseQueryData, id: string) => Promise<void>;
  archiveCaseTemplate: () => Promise<void>;
  restoreCaseTemplate: () => Promise<void>;
}

const DashboardCaseTemplatesShowView = ({
  organization,
  currentEnvironment,
  caseTemplate,
  caseDataManagerSchema,
  caseVariableDefinitions,
  tags,
  users,
  currentUser,
  accountStatuses,
  actionWorkflows,
  dataSources,
  caseQueries,
  enabledInquiryTemplates,
  publishedVerificationTemplates,
  accountTypeFilterOptions,
  graph,
  graphQueryTemplates,
  isLayoutDirty,
  isLoadingCaseVariableDefinitions,
  hasCasesBasic,
  hasCasesEnhanced,
  hasCasesEnterprise,
  updateCaseTemplateV2,
  saveCaseTemplateSettings,
  saveCaseTemplateV2,
  setCaseTemplateIsValid,
  handleCreateCaseQuery,
  handleUpdateCaseQuery,
  archiveCaseTemplate,
  restoreCaseTemplate,
}: DashboardCaseTemplatesShowViewProps) => {
  const isPlatformConnectionDashboardEnabled = useFeatureFlag(
    'global:enable-platform-connection-dashboard',
  );
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lastPath = getOnlyLastPath(pathname);
  const caseTemplateAttributes = caseTemplate?.attributes;

  const matchQueryEditorPath = useRouteMatch({
    path: CASE_QUERY_EDITOR_PATH,
    strict: false,
  });
  useExpandCollapseNavbar();

  const hasCaseTemplateWritePermission = usePermission(Permission.CaseTemplateWrite);
  const [showArchiveCaseTemplateModal, { prompt, confirm, cancel }] = useConfirmation();

  const [
    checklistConfirmationModal,
    { enable: showChecklistConfirmationModal, disable: hideChecklistConfirmationModal },
  ] = useToggle(false);

  const initialValues = useMemo<CaseTemplateSettingsFormData>(() => {
    return {
      'case-template-name': caseTemplateAttributes.name,
      'case-template-prefix': caseTemplateAttributes['case-prefix'],
      'case-creation-email-addresses':
        caseTemplateAttributes.configuration.subscriptions['case-creation-email-addresses'] || [],
      'assignment-policies': caseTemplateAttributes.configuration['assignment-policies'],
      'triage-configuration-unassign-cases-when-unavailable':
        caseTemplateAttributes.configuration['triage-configuration'][
          'unassign-cases-when-unavailable'
        ],
      'triage-configuration-policy-tokens-created-cases':
        caseTemplateAttributes.configuration['triage-configuration']['policy-tokens-created-cases'],
      'triage-configuration-policy-tokens-unassigned-cases':
        caseTemplateAttributes.configuration['triage-configuration'][
          'policy-tokens-unassigned-cases'
        ],
      resolutions: caseTemplateAttributes.configuration.resolutions,
      actions: caseTemplateAttributes.configuration['case-action-ids'],
      states: caseTemplateAttributes.configuration.states,
      'state-tags': caseTemplateAttributes.configuration['state-tags'],
      'field-schema': decorateFieldSchemas({
        fieldSchemas: caseTemplateAttributes.configuration['field-schema'],
      }),
      // FIXME: The new shared fields schema editor expects this in camel case. Potential
      //  in the future for the fields schema editor to be less coupled to the form
      deactivateFieldSchemaKeys: [],
    };
  }, [caseTemplateAttributes]);

  const caseTemplateFormContext = useFormSecurely({
    mode: 'onChange',
    defaultValues: initialValues,
  });

  // Reset the initial values for the form when the initial values get reset (e.g. when the case template
  // is updated) so that the form state `isDirty`, etc. stays up to date.
  useEffect(() => {
    caseTemplateFormContext.reset(initialValues);
  }, [initialValues]);

  const casesDataSchema: JsonSchemaObject =
    generateCaseDataManagerJsonSchema(caseDataManagerSchema);

  const statesFormState: CaseState[] = caseTemplateFormContext.watch('states');

  const environmentName = currentEnvironment?.attributes?.name;

  const caseTemplatePath = `${caseTemplate.id}`;
  const layoutEditorPath = lastPath === caseTemplatePath;
  const navBackPath = layoutEditorPath ? '' : `/${caseTemplatePath}`;

  const caseLayout = caseTemplate.attributes.configuration['case-layout-v2'];
  const fieldSchema = caseTemplate.attributes.configuration['field-schema'];
  const checklistItems = caseTemplate.attributes.configuration['checklist-items'];
  const caseEmailTemplates = caseTemplate.attributes.configuration['case-message-templates'];
  const caseTemplateStates = caseTemplate.attributes.configuration.states;
  const caseCommentTemplates = caseTemplate.attributes.configuration['case-comment-templates'];
  const sarNarrativeTemplates = caseTemplate.attributes.configuration['sar-narrative-templates'];

  const isSettingsFormDirty = caseTemplateFormContext.formState.isDirty;
  const invalidChecklistItemsObject = invalidChecklistItems(
    checklistItems,
    layoutEditorPath,
    statesFormState,
  );

  const isArchived = caseTemplate.attributes.status === CaseTemplateStatus.Archived;

  const handleSave = (layoutEditorPath: boolean) => {
    if (layoutEditorPath) {
      saveCaseTemplateV2();
    } else {
      invalidChecklistItemsObject.invalidChecklistItems.size > 0
        ? showChecklistConfirmationModal()
        : caseTemplateFormContext.handleSubmit(saveCaseTemplateSettings)();
    }
  };

  const updateCaseLayout = (caseLayout: CaseLayout) => {
    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'case-layout-v2': caseLayout,
        },
      },
    });
  };

  const renameCaseTemplate = (name: string) => {
    updateCaseTemplateV2(
      produce(caseTemplate, (draft) => {
        draft.attributes.name = name;
      }),
    );
  };

  // [chris] go/checklist-migration: This method will be deleted after the migration is completed.
  const addChecklistItem = (checklistItem: ChecklistItem) => {
    const nextChecklistItems = [
      ...caseTemplate.attributes.configuration['checklist-items'],
      checklistItem,
    ];

    const fieldSchemaToAdd = {
      key: checklistItem['field-key'],
      label: checklistItem.description,
      type: FieldSchemaType.Boolean,
      config: {},
    } as FieldSchema;

    const nextFieldSchemas = [
      ...caseTemplate.attributes.configuration['field-schema'],
      fieldSchemaToAdd,
    ];

    // [chris] TODO: Need to add rule configuration via 'required-for'. This can be a follow-up PR.
    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'checklist-items': nextChecklistItems,
          'field-schema': nextFieldSchemas,
        },
      },
    });
  };

  const addEmailTemplate = (): CaseEmailTemplate => {
    const addedEmailTemplate: CaseEmailTemplate = {
      name: 'New template',
      token: generateCaseEmailTemplateToken(),
      'creator-id': currentUser.id,
      cc: [],
      subject: '',
      body: '',
    };
    const nextEmailTemplatesList = [
      ...caseTemplate.attributes.configuration['case-message-templates'],
      addedEmailTemplate,
    ];

    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'case-message-templates': nextEmailTemplatesList,
        },
      },
    });

    return addedEmailTemplate;
  };

  const deleteEmailTemplate = (token: string) => {
    const nextEmailTemplatesList = caseTemplate.attributes.configuration[
      'case-message-templates'
    ].filter((template) => template.token !== token);

    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'case-message-templates': nextEmailTemplatesList,
        },
      },
    });
  };

  const updateEmailTemplate = (updatedEmailTemplate: CaseEmailTemplate) => {
    const templateIndex = caseTemplate.attributes.configuration['case-message-templates'].findIndex(
      (template) => template.token === updatedEmailTemplate.token,
    );

    const nextEmailTemplatesList = [
      ...caseTemplate.attributes.configuration['case-message-templates'].slice(0, templateIndex),
      updatedEmailTemplate,
      ...caseTemplate.attributes.configuration['case-message-templates'].slice(templateIndex + 1),
    ];

    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'case-message-templates': nextEmailTemplatesList,
        },
      },
    });
  };

  const addCommentTemplate = () => {
    const nextCommentTemplateList = [
      ...caseTemplate.attributes.configuration['case-comment-templates'],
      {
        name: 'New comment',
        token: generateCaseCommentTemplateToken(),
        body: '',
      },
    ];

    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'case-comment-templates': nextCommentTemplateList,
        },
      },
    });
  };

  const updateCommentTemplate = (updatedCommentTemplate: CaseCommentTemplate) => {
    const templateIndex = caseTemplate.attributes.configuration['case-comment-templates'].findIndex(
      (template) => template.token === updatedCommentTemplate.token,
    );

    const nextCommentTemplatesList = [
      ...caseTemplate.attributes.configuration['case-comment-templates'].slice(0, templateIndex),
      updatedCommentTemplate,
      ...caseTemplate.attributes.configuration['case-comment-templates'].slice(templateIndex + 1),
    ];

    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'case-comment-templates': nextCommentTemplatesList,
        },
      },
    });
  };

  const deleteCommentTemplate = (token: string) => {
    const nextCommentTemplatesList = caseTemplate.attributes.configuration[
      'case-comment-templates'
    ].filter((template) => template.token !== token);

    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'case-comment-templates': nextCommentTemplatesList,
        },
      },
    });
  };

  const reorderCommentTemplateList = (sourceIndex: number, destinationIndex: number) => {
    const commentTemplates = caseTemplate.attributes.configuration['case-comment-templates'];
    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'case-comment-templates': reorder(commentTemplates, sourceIndex, destinationIndex),
        },
      },
    });
  };

  const updateSARNarrativeTemplates = (updatedNarrativeTemplates: SARNarrativeTemplate[]) => {
    updateCaseTemplateV2({
      ...caseTemplate,
      attributes: {
        ...caseTemplate.attributes,
        configuration: {
          ...caseTemplate.attributes.configuration,
          'sar-narrative-templates': updatedNarrativeTemplates,
        },
      },
    });
  };

  const handleArchiveCaseTemplateClick = () => prompt(() => archiveCaseTemplate());
  const handlePlatformConnectionsClick = () =>
    navigate(`/dashboard/platform-connections/${caseTemplate.id}`);

  const SaveButton = () => {
    if (!hasCaseTemplateWritePermission) {
      return (
        <Tooltip
          hideArrow
          side='bottom'
          trigger={
            <Button
              id={`case-templates-show__save-button--${environmentName}`}
              data-test={`case-templates-show__save-button--${environmentName}`}
              onClick={() => null}
              disabled={true}
            >
              Save
            </Button>
          }
        >
          <Paragraph color='primary' style={{ width: '350px' }}>
            {permissionErrorString(Permission.CaseTemplateWrite)}
          </Paragraph>
        </Tooltip>
      );
    }

    if (isArchived) {
      return (
        <Tooltip
          hideArrow
          side='bottom'
          trigger={
            <Button
              id={`case-templates-show__save-button--${environmentName}`}
              data-test={`case-templates-show__save-button--${environmentName}`}
              onClick={() => null}
              disabled={true}
            >
              Save
            </Button>
          }
        >
          <Paragraph color='primary' style={{ width: '350px' }}>
            Archived case templates cannot be edited. Please restore before making changes.
          </Paragraph>
        </Tooltip>
      );
    }

    return (
      <Button
        id={`case-templates-show__save-button--${environmentName}`}
        data-test={`case-templates-show__save-button--${environmentName}`}
        onClick={() => handleSave(layoutEditorPath)}
        disabled={
          !hasCaseTemplateWritePermission ||
          (layoutEditorPath ? !isLayoutDirty : !isSettingsFormDirty)
        }
      >
        Save
      </Button>
    );
  };

  const breadcrumbs = layoutEditorPath
    ? []
    : [
        {
          displayName: capitalize(lastPath),
        },
      ];

  const onBackClick = () => {
    navigate(`/dashboard/case-templates${navBackPath}`);
  };

  return (
    <>
      <Helmet title={`${caseTemplate.attributes.name} - Templates - Cases`} />
      {!matchQueryEditorPath && (
        <FullScreenHeaderContainer data-test='case-templates-show__header'>
          <HorizontalContainer>
            <NavBreadcrumb
              name={caseTemplate.attributes.name}
              breadcrumbItems={breadcrumbs}
              onBackClick={onBackClick}
              onNameUpdate={renameCaseTemplate}
              editable={layoutEditorPath}
            />
            <StatusWrapper>
              <CaseTemplateStatusSticker status={caseTemplate.attributes.status} />
            </StatusWrapper>
          </HorizontalContainer>
          <HStack>
            {SaveButton()}

            <Button
              id={`case-templates-show__settings-button--${environmentName}`}
              data-test={`case-templates-show__settings-button--${environmentName}`}
              to={`/dashboard/case-templates/${caseTemplatePath}/settings`}
            >
              Settings
            </Button>

            <ContextMenu
              variant='inverse'
              data-test={`case-template-show__overflow-menu--${caseTemplate.id}`}
              items={[
                {
                  id: 'platform-connections',
                  onClick: handlePlatformConnectionsClick,
                  hidden: !isPlatformConnectionDashboardEnabled,
                  variant: 'basic' as const,
                  label: 'Connections',
                },
                {
                  id: 'restore',
                  onClick: restoreCaseTemplate,
                  hidden:
                    caseTemplate.attributes.status !== CaseTemplateStatus.Archived ||
                    !hasCaseTemplateWritePermission,
                  variant: 'basic' as const,
                  label: 'Restore',
                },
                {
                  id: 'archive',
                  onClick: handleArchiveCaseTemplateClick,
                  hidden:
                    caseTemplate.attributes.status !== CaseTemplateStatus.Active ||
                    !hasCaseTemplateWritePermission,
                  variant: 'danger' as const,
                  label: 'Archive',
                },
              ]}
              renderItem={(item) => (
                <ContextMenu.Item
                  id={`case-templates-table__row--${caseTemplate.id}__${item.id}`}
                  key={`case-templates-table__row--${caseTemplate.id}__${item.id}`}
                  onClick={(e) => {
                    // Needed to avoid triggering row click below
                    e.stopPropagation();
                    e.preventDefault();
                    item.onClick?.();
                  }}
                  variant={item.variant}
                  data-test={item['data-test']}
                >
                  {item.label}
                </ContextMenu.Item>
              )}
            />
          </HStack>
        </FullScreenHeaderContainer>
      )}
      <SentryRoutes>
        <Route
          index
          element={
            <LayoutEditor<CaseModule, CaseLayoutTab, ModuleTypes>
              organization={organization}
              currentEnvironment={currentEnvironment}
              layoutConfiguration={caseLayout}
              dataSchema={casesDataSchema}
              environmentName={environmentName}
              shouldShowVisibilityToggle={hasCasesBasic || hasCasesEnhanced || hasCasesEnterprise}
              shouldShowVisibilityAdvancedControls={hasCasesEnhanced || hasCasesEnterprise}
              shouldShowAdvancedOptions={hasCasesEnhanced || hasCasesEnterprise}
              supportedModuleTypes={CasesGridModuleTypes}
              supportedComponentTypes={CasesGridComponentTypes}
              generateDefaultModuleConfiguration={generateDefaultCaseModuleConfiguration}
              updateLayoutConfiguration={updateCaseLayout}
              setCaseTemplateIsValid={setCaseTemplateIsValid}
              data-test='case-templates-show__layout-editor'
              renderModule={(module) => (
                <CaseModuleBasePreview
                  key={module.token}
                  fieldSchema={fieldSchema}
                  checklistItems={checklistItems}
                  enabledInquiryTemplates={enabledInquiryTemplates}
                  caseTemplateStates={caseTemplateStates}
                  caseVariableDefinitions={caseVariableDefinitions}
                  module={module}
                />
              )}
              renderTabForm={(selectedTab, hideEditModal, handleEdit, handleValidateTabName) => (
                <FormEditCaseLayoutTab
                  tab={selectedTab}
                  cancel={hideEditModal}
                  submit={handleEdit}
                  dataSchema={casesDataSchema}
                  validateTabName={handleValidateTabName}
                />
              )}
              renderModuleConfiguration={(module, handleUpdateModule) =>
                renderCasesModuleConfigurationPanel({
                  module,
                  handleUpdateModule,
                  actionWorkflows,
                  caseCommentTemplates,
                  caseVariableDefinitions,
                  addCommentTemplate,
                  updateCommentTemplate,
                  deleteCommentTemplate,
                  reorderCommentTemplateList,
                  caseEmailTemplates,
                  addEmailTemplate,
                  updateEmailTemplate,
                  deleteEmailTemplate,
                  checklistItems,
                  caseTemplateStates,
                  fieldSchema,
                  sarNarrativeTemplates,
                  updateSARNarrativeTemplates,
                  addChecklistItem,
                  casesDataSchema: casesDataSchema,
                  accountStatuses,
                  caseQueries,
                  graph,
                  graphQueryTemplates,
                  enabledInquiryTemplates,
                  publishedVerificationTemplates,
                  accountTypeFilterOptions,
                })
              }
            />
          }
        />
        <Route
          path='settings'
          element={
            <FormProvider {...caseTemplateFormContext}>
              <CaseTemplateSettings
                caseTemplateId={caseTemplate.id}
                caseTemplateAttributes={caseTemplate.attributes}
                currentEnvironment={currentEnvironment}
                caseDataManagerSchema={caseDataManagerSchema}
                caseQueries={caseQueries}
                caseVariableDefinitions={caseVariableDefinitions}
                dataSources={dataSources}
                initialFieldSchema={fieldSchema}
                tags={tags}
                users={users}
                actionWorkflows={actionWorkflows}
                hasCasesBasic={hasCasesBasic}
                hasCasesEnhanced={hasCasesEnhanced}
                hasCasesEnterprise={hasCasesEnterprise}
                updateCaseTemplate={saveCaseTemplateSettings}
                handleCreateCaseQuery={handleCreateCaseQuery}
                handleUpdateCaseQuery={handleUpdateCaseQuery}
              />
            </FormProvider>
          }
        />
        <Route
          path='settings/queries/:caseVariableDefinitionId'
          element={
            <FormProvider {...caseTemplateFormContext}>
              <CaseTemplateQueryEditor
                caseTemplate={caseTemplate}
                caseVariableDefinitions={caseVariableDefinitions}
                isLoadingCaseVariableDefinitions={isLoadingCaseVariableDefinitions}
                dataSources={dataSources}
                hasCasesBasic={hasCasesBasic}
                hasCasesEnhanced={hasCasesEnhanced}
                hasCasesEnterprise={hasCasesEnterprise}
              />
            </FormProvider>
          }
        />
        <Route path='*' element={<Navigate to='.' replace />} />
      </SentryRoutes>
      <ModalWrapper
        isOpen={checklistConfirmationModal}
        handleCloseModal={hideChecklistConfirmationModal}
      >
        <ConfirmationModal
          cancelAction={hideChecklistConfirmationModal}
          confirmAction={caseTemplateFormContext.handleSubmit(saveCaseTemplateSettings)}
          cancelActionText='Don’t save'
          confirmActionText={
            invalidChecklistItemsObject.invalidChecklistItems.size > 0 ? 'Confirm Changes' : 'Save'
          }
          header='Save changes?'
          confirmButtonType='primary'
        >
          <AutocorrectChecklistItemsDescription
            states={caseTemplateStates}
            invalidStates={invalidChecklistItemsObject.invalidStates}
            invalidChecklistItems={invalidChecklistItemsObject.invalidChecklistItems}
          />
        </ConfirmationModal>
      </ModalWrapper>

      <ModalWrapper isOpen={showArchiveCaseTemplateModal} handleCloseModal={cancel}>
        <ConfirmationModal
          cancelAction={cancel}
          confirmAction={confirm}
          header='Archive this case template?'
          confirmActionText='Archive'
        >
          <p>
            You will no longer be able to create cases with this case template. Please ensure all
            references to this template are removed from your workflows.
          </p>
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

const CaseTemplateStatusSticker = ({ status }: { status: CaseTemplateStatus }) => {
  if (status === CaseTemplateStatus.Active) {
    return <Sticker variant='success'>Active</Sticker>;
  } else {
    return <Sticker variant='basic'>Archived</Sticker>;
  }
};

export const renderCasesModuleConfigurationPanel = ({
  module,
  handleUpdateModule,
  actionWorkflows,
  caseCommentTemplates,
  caseVariableDefinitions,
  addCommentTemplate,
  updateCommentTemplate,
  deleteCommentTemplate,
  reorderCommentTemplateList,
  caseEmailTemplates,
  addEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  checklistItems,
  addChecklistItem,
  caseTemplateStates,
  fieldSchema,
  sarNarrativeTemplates,
  updateSARNarrativeTemplates,
  casesDataSchema,
  accountStatuses,
  caseQueries,
  graph,
  graphQueryTemplates,
  enabledInquiryTemplates,
  publishedVerificationTemplates,
  accountTypeFilterOptions,
}: {
  module: CaseModule;
  handleUpdateModule: (module: CaseModule) => void;
  actionWorkflows: Workflow[];
  caseCommentTemplates: CaseCommentTemplate[];
  caseVariableDefinitions: CaseVariableDefinition[];
  addCommentTemplate: () => void;
  updateCommentTemplate: (updateCommentTemplate: CaseCommentTemplate) => void;
  deleteCommentTemplate: (token: string) => void;
  reorderCommentTemplateList: (sourceIndex: number, destinationIndex: number) => void;
  caseEmailTemplates: CaseEmailTemplate[];
  addEmailTemplate: () => CaseEmailTemplate;
  updateEmailTemplate: (updateEmailTemplate: CaseEmailTemplate) => void;
  deleteEmailTemplate: (token: string) => void;
  checklistItems: ChecklistItem[];
  addChecklistItem: (checklistItem: ChecklistItem) => void;
  caseTemplateStates: CaseState[];
  fieldSchema: FieldSchema[];
  sarNarrativeTemplates: SARNarrativeTemplate[];
  updateSARNarrativeTemplates: (templates: SARNarrativeTemplate[]) => void;
  casesDataSchema: JsonSchemaObject;
  accountStatuses: AccountStatus[];
  caseQueries: CaseQuery[];
  graph: Graph;
  graphQueryTemplates: GraphQueryTemplate[];
  enabledInquiryTemplates: (InquiryTemplate | Template)[];
  publishedVerificationTemplates: VerificationTemplate[];
  accountTypeFilterOptions: AccountTypesFilterOption[];
}) => {
  switch (module.type) {
    case ModuleTypes.AISummary:
      return (
        <AISummaryPanel key={module.token} module={module} updateModule={handleUpdateModule} />
      );
    case ModuleTypes.Button:
      return (
        <ButtonPanel
          key={module.token}
          actionWorkflows={actionWorkflows}
          module={module}
          updateModule={handleUpdateModule}
        />
      );
    case ModuleTypes.CaseComments:
      return (
        <CaseCommentPanel
          key={module.token}
          caseCommentTemplates={caseCommentTemplates}
          addCommentTemplate={addCommentTemplate}
          updateCommentTemplate={updateCommentTemplate}
          deleteCommentTemplate={deleteCommentTemplate}
          reorderCommentTemplateList={reorderCommentTemplateList}
        />
      );
    case ModuleTypes.CaseEmail:
      return (
        <CaseEmailPanel
          key={module.token}
          module={module}
          caseEmailTemplates={caseEmailTemplates}
          updateModule={handleUpdateModule}
          addEmailTemplate={addEmailTemplate}
          deleteEmailTemplate={deleteEmailTemplate}
          updateEmailTemplate={updateEmailTemplate}
        />
      );
    case ModuleTypes.CaseInfo:
      return <CaseInfoPanel key={module.token} module={module} updateModule={handleUpdateModule} />;
    case ModuleTypes.Checklist:
      return (
        <ChecklistPanel
          key={module.token}
          module={module}
          checklistItems={checklistItems}
          states={caseTemplateStates}
          fieldSchemas={fieldSchema}
          updateModule={handleUpdateModule}
          addChecklistItem={addChecklistItem}
        />
      );
    case ModuleTypes.DataDisplay: {
      return (
        <DataDisplayPanel
          key={module.token}
          module={module}
          dataSchema={casesDataSchema}
          updateModule={handleUpdateModule}
        />
      );
    }
    case ModuleTypes.DataTable: {
      return (
        <DataTablePanel
          key={module.token}
          module={module}
          updateModule={handleUpdateModule}
          variableDefinitions={caseVariableDefinitions}
        />
      );
    }
    case ModuleTypes.Device:
      return <DevicePanel key={module.token} module={module} updateModule={handleUpdateModule} />;
    case ModuleTypes.KeyValue:
      return (
        <KeyValuePanel
          key={module.token}
          module={module}
          fieldSchema={fieldSchema}
          updateModule={handleUpdateModule}
        />
      );
    case ModuleTypes.Graphv2:
      return (
        <GraphV2Panel
          accountStatuses={accountStatuses}
          caseQueries={caseQueries}
          key={module.token}
          module={module}
          graph={graph}
          graphQueryTemplates={graphQueryTemplates}
          updateModule={handleUpdateModule}
          fieldSchema={fieldSchema}
        />
      );
    case ModuleTypes.Input:
      return (
        <InputPanel
          key={module.token}
          module={module}
          fieldSchema={fieldSchema}
          updateModule={handleUpdateModule}
        />
      );
    case ModuleTypes.InputSelect:
      return (
        <InputSelectPanel
          key={module.token}
          module={module}
          fieldSchema={fieldSchema}
          updateModule={handleUpdateModule}
        />
      );
    case ModuleTypes.Iframe:
      return (
        <IframePanel
          key={module.token}
          module={module}
          dataSchema={casesDataSchema}
          updateModule={handleUpdateModule}
        />
      );
    case ModuleTypes.InsightsSummary:
      return (
        <InsightsSummaryPanel
          key={module.token}
          module={module}
          dataSchema={casesDataSchema}
          updateModule={handleUpdateModule}
        />
      );
    case ModuleTypes.LayoutByAccount:
      return (
        <LayoutByAccountPanel
          key={module.token}
          module={module}
          accountTypeFilterOptions={accountTypeFilterOptions}
          updateModule={handleUpdateModule}
        />
      );
    case ModuleTypes.Location:
      return <LocationPanel key={module.token} module={module} updateModule={handleUpdateModule} />;
    case ModuleTypes.Markdown:
      return <MarkdownPanel key={module.token} module={module} updateModule={handleUpdateModule} />;
    case ModuleTypes.Resource:
      switch (module['field-key']) {
        case 'accounts':
          return (
            <ResourceAccountPanel
              key={module.token}
              module={module as ModuleResourceAccount}
              accountTypeFilterOptions={accountTypeFilterOptions}
              updateModule={handleUpdateModule}
            />
          );
        case 'inquiries':
          return (
            <ResourceInquiryPanel
              key={module.token}
              enabledInquiryTemplates={enabledInquiryTemplates}
              module={module as ModuleResourceInquiry}
              updateModule={handleUpdateModule}
            />
          );
        case 'reports':
          return (
            <ResourceReportPanel
              key={module.token}
              module={module as ModuleResourceReport}
              updateModule={handleUpdateModule}
            />
          );
        case 'report-runs':
          return (
            <ResourceReportRunPanel
              key={module.token}
              module={module as ModuleResourceReportRun}
              updateModule={handleUpdateModule}
            />
          );
        case 'transactions':
          return (
            <ResourceTransactionPanel
              key={module.token}
              module={module as ModuleResourceTransaction}
              updateModule={handleUpdateModule}
            />
          );
        case 'verifications':
          return (
            <ResourceVerificationPanel
              key={module.token}
              publishedVerificationTemplates={publishedVerificationTemplates}
              module={module as ModuleResourceVerification}
              updateModule={handleUpdateModule}
            />
          );
      }
      break;
    case ModuleTypes.Sar:
      return (
        <SARPanel
          key={module.token}
          sarNarrativeTemplates={sarNarrativeTemplates}
          updateSARNarrativeTemplates={updateSARNarrativeTemplates}
        />
      );
    case ModuleTypes.Table:
      return (
        <TablePanel
          key={module.token}
          dataSchema={casesDataSchema}
          caseQueries={caseQueries}
          module={module}
          updateModule={handleUpdateModule}
        />
      );
    default: {
      return null;
    }
  }
};

export default DashboardCaseTemplatesShowView;
